import { useMemo } from 'react';
import type { DataTableMode, DataTablePagination } from '@odo/shared-ui';
import {
    useIteroOrders,
    useIteroOrdersPage,
    useIteroOrdersScrollReset,
    type IteroBrowseScope
} from '../../../../hooks/useIteroBrowse';
import type { IteroOrder, IteroOrdersFilterParams } from '../../../../services/iteroBrowse';
import { browseErrorText } from './browseErrorText';
import { openEndedPageCount, pageScope, scrollRowsMeta } from './browsePaging';
import { useIteroBrowserScope } from './iteroBrowserScope';
import {
    DEFAULT_ORDER_FILTERS,
    buildOrderFilterParams,
    isDefaultOrderFilters,
    mergeOrderPages,
    orderFiltersError,
    orderPageParams,
    type OrderFilterValues
} from './orderQuery';
import { useBrowseFilters, useScopedPage } from './useBrowseFilters';

const NO_ORDERS: readonly IteroOrder[] = [];
const noop = (): void => undefined;

export interface OrdersBrowse {
    filters: OrderFilterValues;
    updateFilters: (patch: Partial<OrderFilterValues>) => void;
    clearFilters: () => void;
    rangeError: string | null;
    showClear: boolean;
    mode: DataTableMode;
    orders: readonly IteroOrder[];
    // Pages mode only: iTero reports no total, so the pager offers Next while more exist.
    pagination: DataTablePagination | undefined;
    isLoading: boolean;
    isUpdating: boolean;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
    errorMessage: string | null;
    loadMoreError: string | null;
    retry: () => void;
    resetScroll: () => void;
    // Changes whenever the list starts over (filters, search, mode).
    listKey: string;
}

interface OrderRows {
    orders: readonly IteroOrder[];
    isPending: boolean;
    isShowingPrevious: boolean;
    error: unknown;
    loadMoreError: unknown;
    hasMore: boolean;
    isLoadingMore: boolean;
    loadMore: () => void;
    retry: () => void;
}

function usePagedOrders(
    scope: IteroBrowseScope,
    filterParams: IteroOrdersFilterParams,
    page: number,
    enabled: boolean
): OrderRows {
    const params = useMemo(() => orderPageParams(filterParams, page), [filterParams, page]);
    const query = useIteroOrdersPage(scope, params, enabled);
    return {
        orders: query.data?.orders ?? NO_ORDERS,
        isPending: query.isPending,
        isShowingPrevious: query.isFetching && query.isPlaceholderData,
        error: query.isError ? query.error : null,
        loadMoreError: null,
        hasMore: Boolean(query.data?.hasMore),
        isLoadingMore: false,
        loadMore: noop,
        retry: () => void query.refetch()
    };
}

function useScrolledOrders(
    scope: IteroBrowseScope,
    filterParams: IteroOrdersFilterParams,
    enabled: boolean
): OrderRows {
    const query = useIteroOrders(scope, filterParams, enabled);
    const orders = useMemo(() => mergeOrderPages(query.data?.pages ?? []), [query.data]);
    return { orders, ...scrollRowsMeta(query) };
}

// enabled stays false until the Orders tab is first opened, so the modal never fetches it unseen.
export function useOrdersBrowse(
    today: string,
    enabled: boolean,
    mode: DataTableMode
): OrdersBrowse {
    const scope = useIteroBrowserScope();
    const { filters, effective, isTyping, updateFilters, clearFilters } =
        useBrowseFilters<OrderFilterValues>(DEFAULT_ORDER_FILTERS);
    const rangeError = orderFiltersError(effective);
    const filterParams = useMemo(
        () => buildOrderFilterParams(effective, today),
        [effective, today]
    );
    const canFetch = enabled && rangeError === null;
    const scopeKey = pageScope(mode, JSON.stringify(filterParams));
    const [page, setPage] = useScopedPage(scopeKey);

    const paged = usePagedOrders(scope, filterParams, page, canFetch && mode === 'paged');
    const scrolled = useScrolledOrders(scope, filterParams, canFetch && mode === 'infinite');
    const rows = mode === 'paged' ? paged : scrolled;
    const resetScroll = useIteroOrdersScrollReset(scope);

    return {
        filters,
        updateFilters,
        clearFilters,
        rangeError,
        showClear: !isDefaultOrderFilters(filters),
        mode,
        orders: rows.orders,
        pagination:
            mode === 'paged'
                ? { page, totalPages: openEndedPageCount(page, rows.hasMore), onPage: setPage }
                : undefined,
        // A disabled query (bad range) is "pending" forever; show the empty table, not a skeleton.
        isLoading: rows.isPending && canFetch,
        isUpdating: rows.isShowingPrevious || isTyping,
        hasMore: mode === 'infinite' && rows.hasMore,
        isLoadingMore: rows.isLoadingMore,
        loadMore: rows.loadMore,
        errorMessage: rows.error ? browseErrorText(rows.error, "Couldn't load iTero orders") : null,
        loadMoreError: rows.loadMoreError
            ? browseErrorText(rows.loadMoreError, "Couldn't load more orders")
            : null,
        retry: rows.retry,
        resetScroll: () => resetScroll(filterParams),
        listKey: scopeKey
    };
}
